/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useRef, useState } from "react"
import Tippy from "@tippyjs/react"
import { ConditionalWrapper } from "@cd2/components"
import { useTheme } from "@cd2/theme"
import { IComponentProp } from "@cd2/tools/build/es6"

interface IProps {
  children: (state: {
    isOpen: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  }) => React.ReactNode
  content: (state: {
    isOpen: boolean
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  }) => React.ReactNode
  className?: string
  padding?: number
  canCloseOnClickOutside?: boolean
  onClickOutside?: () => void
  noDivWrapper?: boolean
  placement?: IComponentProp<typeof Tippy, "placement">
  arrow?: IComponentProp<typeof Tippy, "arrow">
  maxWidth?: number | string
  popOutZIndex?: number
  tippyBackgroundColor?: string
  offset?: IComponentProp<typeof Tippy, "offset">
}

export function InteractivePopout({
  children,
  content,
  className,
  padding,
  canCloseOnClickOutside = true,
  onClickOutside,
  noDivWrapper,
  placement,
  arrow,
  maxWidth,
  popOutZIndex,
  tippyBackgroundColor,
  // adding offset seems to have shifted the popout from it's original placement
  // - default 8 px down offset to account for this change
  offset = [0, 8],
}: IProps) {
  const theme = useTheme()

  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  return (
    <ConditionalWrapper Wrapper={"div" as any} condition={!noDivWrapper}>
      <Tippy
        maxWidth={maxWidth}
        arrow={arrow}
        animation="fade"
        zIndex={popOutZIndex || 99}
        appendTo={document.body}
        content={<div ref={ref}>{isOpen && content({ isOpen, setIsOpen })}</div>}
        onClickOutside={() => {
          if (isOpen) {
            if (canCloseOnClickOutside) {
              setIsOpen(false)
            }
            onClickOutside?.()
          }
        }}
        css={css`
          &.tippy-box[data-animation="fade"][data-state="hidden"] {
            opacity: 0;
          }

          [data-tippy-root] {
            max-width: calc(100vw - 10px);
          }

          &.tippy-box {
            background-color: ${tippyBackgroundColor ?? "white"};
            color: #333;
            border-radius: 4px;
            font-size: 13px;
            line-height: 1.4;
            outline: 0;
            transition-property: transform, visibility, opacity;
            box-shadow: ${theme.boxShadow.sm};
            width: 100%;
          }

          &.tippy-box[data-placement^="bottom"] > .tippy-arrow {
            top: 0;
          }

          &.tippy-box[data-placement^="bottom"] > .tippy-arrow:before {
            top: -7px;
            left: 0;
            border-width: 0 8px 8px;
            border-bottom-color: initial;
            transform-origin: center bottom;
          }

          &.tippy-box[data-inertia][data-state="visible"] {
            transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
          }

          .tippy-arrow {
            width: 16px;
            height: 16px;
            color: white;
            filter: drop-shadow(0 -1.5px 0.5px rgb(0 0 0 / 5%));
          }

          .tippy-arrow:before {
            content: "";
            position: absolute;
            border-color: transparent;
            border-style: solid;
          }

          .tippy-content {
            position: relative;
            z-index: 1;
            padding: ${padding}px;
          }
        `}
        placement={placement ?? "bottom-start"}
        visible={isOpen}
        offset={offset}
        interactive
      >
        <div className={className}>{children({ isOpen, setIsOpen })}</div>
      </Tippy>
    </ConditionalWrapper>
  )
}
